import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { RotateCcw, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * DemoResetButton — Admin-only header affordance that re-seeds the demo
 * assessments. Not shown for non-admin roles.
 */
const DemoResetButton = ({ role, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  if (role !== 'System Administrator') return null;

  const runReset = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/dashboard/demo/reset`);
      const { deleted, seeded, duration_ms } = res.data || {};
      toast.success(`Demo reset · ${deleted} removed · ${seeded} seeded · ${Math.round((duration_ms || 0) / 100) / 10}s`);
      setOpen(false);
      if (onDone) onDone();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to reset demo data';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-amber-700 hover:text-amber-800 hover:bg-amber-50"
          data-testid="demo-reset-btn"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Demo Reset
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="demo-reset-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Reset demo assessments?</AlertDialogTitle>
          <AlertDialogDescription>
            This wipes all existing assessments and re-seeds the demo set (≈80 realistic cases across 3 financial years).
            Farmer profiles are preserved. This is intended for stage demos only.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="demo-reset-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={(e) => { e.preventDefault(); runReset(); }}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="demo-reset-confirm-btn"
          >
            {busy ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Re-seeding…</> : 'Reset and re-seed'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DemoResetButton;
